import { ImageLoadingState } from "./image-loading.enum";
import ImageViewerProps from "./ImageViewerProps.interface";
import preloadImage from "./helpers/preload-image";
import styles from "./ImageViewer.module.scss";

import { useInView } from "react-intersection-observer";
import combineClasses from "helpers/combine-classes";
import isBrowserContext from "helpers/isBrowserContext";
import React, { FunctionComponent, useEffect, useState } from "react";
import setImageOptions from "app/media/helpers/setImageOptions";

const ImageViewer: FunctionComponent<ImageViewerProps> = (props): JSX.Element => {
    const { ref, inView, entry } = useInView({ triggerOnce: true, threshold: 0.1 });
    const [imageUrl, setImageUrl] = useState<string>();
    const [imageLoadingState, setImageLoadingState] = useState<ImageLoadingState>(ImageLoadingState.NONE);

    const boundsClass = props.type === "containX" ? styles.contain_wide : props.type === "containY" ? styles.contain_long : props.type === "contain" ? (props.aspectRatioX && props.aspectRatioY && props.aspectRatioX > props.aspectRatioY ? styles.contain_wide : styles.contain_long) : undefined;
    const fallbackUrl = setImageOptions(props.src, 400, props.extensionType, props.crop, props.focalPoint, props.aspectRatioX && props.aspectRatioY ? (props.aspectRatioY / props.aspectRatioX) : undefined);

    const aspectRatioStyle = (props.aspectRatioX && props.aspectRatioY) ? { aspectRatio: `${props.aspectRatioX}/${props.aspectRatioY}` } : undefined;
    const onLoadedClass = imageLoadingState === ImageLoadingState.LOADED ? props.loadedClassName : undefined;

    {/* TODO: Update on resize */ }

    useEffect(() => {
        if (entry && inView === true) {
            const boundingClientRect = entry.target.getBoundingClientRect();
            const boundingClientWidth = boundingClientRect?.width;
            const boundingClientHeight = boundingClientRect?.height;

            if (isBrowserContext() && boundingClientWidth && boundingClientWidth > 0) {
                const clientWidth = boundingClientWidth * (window?.devicePixelRatio || 1);
                const clientHeight = boundingClientHeight * (window?.devicePixelRatio || 1);

                let heightRatio = clientHeight / clientWidth;

                if (props.sourceWidth && props.sourceHeight && props.type === "contain") {
                    heightRatio = (props.sourceHeight / props.sourceWidth);
                }
                else if (props.aspectRatioX && props.aspectRatioY) {
                    heightRatio = (props.aspectRatioY / props.aspectRatioX);
                }

                const imageUrlWithResizeOptions = setImageOptions(props.src, clientWidth, props.extensionType, props.crop, props.focalPoint, heightRatio);
                setImageUrl(imageUrlWithResizeOptions);

                preloadImage(imageUrlWithResizeOptions, setImageLoadingState);
            }
        }
    }, [entry?.target, inView]);

    return (
        <div className={combineClasses(styles.wrapper, boundsClass, onLoadedClass, props.className)} style={aspectRatioStyle} ref={ref}>
            <div data-name="inner" className={styles.inner}>
                {/* TODO: Set Schema ImageObject */}
                {/* Image */}
                {isBrowserContext() && imageLoadingState === ImageLoadingState.LOADED ? (
                    <img data-name="image" className={styles.image} src={imageUrl} alt={props.alt} />
                ) : null}

                {!isBrowserContext() && props.src ? (
                    <img className={styles.image} src={fallbackUrl} alt={props.alt} loading="lazy" />
                ) : null}

                {/* Loading */}
                {imageLoadingState === ImageLoadingState.LOADING ? (
                    <div className={styles.loading}>Loading</div>
                ) : null}

                {/* Error */}
                {imageLoadingState === ImageLoadingState.ERROR ? (
                    <div className={styles.error}>Error</div>
                ) : null}

                {/* TODO: Set the .ratio background color based on the backgroundColor from the media */}
                {typeof (props.aspectRatioX) !== "undefined" && typeof (props.aspectRatioY) !== "undefined" ? (
                    <svg viewBox={`0 0 ${props.aspectRatioX} ${props.aspectRatioY}`} version="1.1" className={styles.ratio} preserveAspectRatio="xMidYMid" xmlSpace="preserve"></svg>
                ) : null}
            </div>
        </div>
    );
};

export default ImageViewer;